/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Observable } from "@vienna-hypertext/observable";

import { API } from "src/classes/api";
import { BasePlate } from "src/classes/plate/BasePlate/BasePlate";
import { Plate } from "src/classes/plate/Plate";
import { BaseUser } from "src/classes/user";
import { Toaster } from "src/classes/utilities/Toaster";
import { stringToHSL } from "src/lib/utils/color";

import { NotificationsSocket } from "./socket/NotificationSocket";
import { CurrentUserSnapshot, RequiredUserMethods } from "./user.interfaces";

export class CurrentUser extends Observable<CurrentUserSnapshot> implements RequiredUserMethods {
  private static avatarDomain = `https://vienna-${
    process.env.ENVIRONMENT === "prod" ? "prod" : "dev"
  }-avatar.imgix.net`;

  private socket: NotificationsSocket;

  private constructor(snapshot: CurrentUserSnapshot) {
    super(snapshot);
    this.socket = new NotificationsSocket(this);
  }

  /* -------------------------------------------------- */
  /*                        State                       */
  /* -------------------------------------------------- */
  public async saveProfileChanges(
    updates: Pick<CurrentUserSnapshot, "bio" | "displayName" | "avatarName">
  ): Promise<void> {
    try {
      await API.users.updateProfile(this.snapshot.id, updates);
      this.setState({ ...updates });
    } catch (e) {
      Toaster.error("Failed to save profile changes");
    }
  }

  /* -------------------------------------------------- */
  /*                  Follow/Unfollow                   */
  /* -------------------------------------------------- */
  public async follow(user: BaseUser): Promise<void> {
    try {
      await API.users.follow(user.snapshot.id);
      this.setState({ following: [...this.snapshot.following, user] });
    } catch (e) {
      Toaster.error(`Failed to follow ${user.snapshot.username}`);
    }
  }

  public async unfollow(user: BaseUser): Promise<void> {
    try {
      await API.users.follow(user.snapshot.id);
      this.setState({
        following: [...this.snapshot.following.filter((followed) => followed.snapshot.id !== user.snapshot.id)],
      });
    } catch (e) {
      Toaster.error(`Failed to unfollow ${user.snapshot.username}`);
    }
  }

  /* -------------------------------------------------- */
  /*                      Invites                       */
  /* -------------------------------------------------- */
  public consumeInvite(): void {
    this.setState({ remainingInvites: this.snapshot.remainingInvites-- });
  }

  /* -------------------------------------------------- */
  /*                       Plates                       */
  /* -------------------------------------------------- */

  public async getPlates(): Promise<void> {
    const plates = await API.users.getPlates();
    this.setState({ plates: [...plates] });
  }

  public async getSharedPlates(): Promise<void> {
    const sharedPlates = await API.users.getSharedPlates();
    this.setState({ sharedPlates: [...sharedPlates] });
  }

  public async deletePlate(plate: Plate | BasePlate): Promise<void> {
    await plate.delete();
    this.setState({ plates: [...this.snapshot.plates.filter((p) => p.snapshot.id !== plate.snapshot.id)] });
  }

  /* -------------------------------------------------- */
  /*                    Notifications                    */
  /* -------------------------------------------------- */
  public async getNotifications(): Promise<void> {
    const notifications = await API.notifications.get();
    this.setState({ notifications: [...notifications], unreadNotificationCount: 0 });
  }

  public handleNotification(): void {
    Toaster.success("New notification!");
    this.setState({ unreadNotificationCount: this.snapshot.unreadNotificationCount + 1 });
  }

  /* -------------------------------------------------- */
  /*                        Likes                       */
  /* -------------------------------------------------- */
  public async getLikedPlates(): Promise<void> {
    const likedPlates = await API.users.getLikedPlates();
    this.setState({ likedPlates: [...likedPlates] });
  }

  public async getLikedBlocks(cursor: string | null): Promise<string> {
    const res = await API.blocks.getLiked(cursor);
    this.setState({ likedBlocks: [...this.snapshot.likedBlocks, ...res.blocks] });
    return res.cursor;
  }

  /* -------------------------------------------------- */
  /*              Link, Color, Username                 */
  /* -------------------------------------------------- */
  public get inAppLink(): string {
    return `/user/${this.snapshot.username}`;
  }

  public get color(): string {
    return stringToHSL(this.snapshot.username, { lit: [70, 80] });
  }

  public get prefixedUsername(): string {
    return `${this.snapshot.registeredWithUrbit ? "" : "@"}${this.snapshot.username}`;
  }

  public get readableDateJoined(): string {
    const date = new Date(this.snapshot.createdAt);
    return `Joined ${date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })}`;
  }

  public avatarURL(size: number): string {
    const isGif = this.snapshot.avatarName.endsWith(".gif");
    return `${CurrentUser.avatarDomain}/${this.snapshot.avatarName}${
      isGif ? "" : `?fit=max&h=${size * 2}&w=${size * 2}`
    }`;
  }

  /* -------------------------------------------------- */
  /*                  Handle Logout                    */
  /* -------------------------------------------------- */
  public onLogout(): void {
    this.socket.stopListeningAndClose();
  }

  /* -------------------------------------------------- */
  /*                  Static Methods                    */
  /* -------------------------------------------------- */
  public static fromSnapshot(snapshot: CurrentUserSnapshot): CurrentUser {
    return new CurrentUser(snapshot);
  }
}
